"use client";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { unwrapApiData } from "@/lib/apiResponse";

import { useEffect, useState } from "react";

interface Announcement {
  id: string;
  message: string;
  redirect_url?: string;
  session_id: string;
  target_audience: 'ALL' | 'MEMBERS' | 'NON_MEMBERS';
}

type AnnouncementPayload = Partial<Announcement> & {
  announcementId?: string | number;
  redirectUrl?: string;
  targetAudience?: Announcement['target_audience'];
};

function normalizeAnnouncement(payload: unknown): Announcement | null {
  const data = unwrapApiData<unknown>(payload);
  const candidate = Array.isArray(data)
    ? data[0]
    : data && typeof data === 'object' && 'announcement' in data
      ? (data as { announcement?: unknown }).announcement
      : data;

  if (!candidate || typeof candidate !== 'object') return null;
  const value = candidate as AnnouncementPayload;
  const id = value.id ?? value.announcementId;
  if (id === undefined || !value.message) return null;

  return {
    id: String(id),
    message: value.message,
    redirect_url: value.redirect_url ?? value.redirectUrl,
    session_id: value.session_id ?? String(id),
    target_audience: value.target_audience ?? value.targetAudience ?? 'ALL',
  };
}

export function AnnouncementBanner() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    const refresh = () => setRefreshNonce((value) => value + 1);
    window.addEventListener('seexr:resume', refresh);
    return () => window.removeEventListener('seexr:resume', refresh);
  }, []);

  useEffect(() => {
    async function fetchActiveAnnouncement() {
      try {
        const token = document.cookie.split('; ').find(row => row.startsWith('seexr_access='))?.split('=')[1];
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetchWithAuth(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000'}/api/announcements/active`, { headers, credentials: "include" });
        if (res.ok) {
          const activeAnn = normalizeAnnouncement(await res.json());

          if (activeAnn) {
            // Core의 active 응답은 공개 경로라 로그인 사용자 여부와 관계없이
            // 로컬 dismiss 상태도 함께 확인한다.
            const localDismissed = localStorage.getItem(`dismissed_announcement_${activeAnn.session_id}`);
            if (localDismissed) {
              setLoading(false);
              return;
            }
            
            setAnnouncement(activeAnn);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchActiveAnnouncement();
  }, [refreshNonce]);

  const handleDismiss = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!announcement) return;

    // UI상 즉시 숨김
    const currentAnn = announcement;
    setAnnouncement(null);
    localStorage.setItem(`dismissed_announcement_${currentAnn.session_id}`, 'true');

    const hasToken = document.cookie.includes('seexr_refresh') || localStorage.getItem('seexr_token');
    
    if (hasToken) {
      // 회원인 경우 DB에 저장
      try {
        const token = document.cookie.split('; ').find(row => row.startsWith('seexr_access='))?.split('=')[1];
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        await fetchWithAuth(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000'}/api/announcements/${currentAnn.id}/dismiss`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ session_id: currentAnn.session_id })
        });
      } catch (err) {
        console.error('Failed to dismiss announcement', err);
      }
    } else {
      // 비회원인 경우 쿠키에도 저장
      // 쿠키에도 저장 요청에 따라
      document.cookie = `dismissed_announcement_${currentAnn.session_id}=true; path=/; max-age=31536000`;
    }
  };

  const handleActionClick = () => {
    if (announcement?.redirect_url) {
      window.location.href = announcement.redirect_url;
    }
  };

  // 로딩 중이거나 응답 결과가 없으면 헤더 자체를 렌더링하지 않음.
  // 스피너 헤더를 먼저 노출했다가 사라지게 하면 레이아웃 점프 + 깜빡임이 발생해서 UX가 거슬림.
  if (loading || !announcement) {
    return null;
  }

  // 공지사항 렌더링
  return (
    <header 
       onClick={handleActionClick}
       className={`relative w-full z-10 h-[50px] shrink-0 flex items-center px-6 transition-all duration-300 bg-seexr-solid/10 dark:bg-seexr-solid/20 backdrop-blur-[20px] border-b border-seexr-primary/20 ${announcement.redirect_url ? 'cursor-pointer hover:bg-seexr-solid/15 dark:hover:bg-seexr-solid/30' : ''}`}
    >
      <div className="w-full flex items-center justify-center text-center px-8 relative">
        <div className="flex items-center gap-3">
           <span className="w-6 h-6 rounded-full bg-seexr-solid flex items-center justify-center text-white shrink-0">
             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
           </span>
           <span className="font-bold text-[14px] text-gray-900 dark:text-gray-100">{announcement.message}</span>
           {announcement.redirect_url && (
             <span className="text-[12px] font-bold px-2 py-0.5 ml-2 bg-seexr-solid text-white rounded-full flex gap-1 items-center hover:bg-seexr-hover transition-colors">
               자세히 보기 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
             </span>
           )}
        </div>
        
        {/* 오른쪽 끝 닫기 버튼 */}
        <button 
          onClick={handleDismiss}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-black/5 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white transition-all focus:outline-none"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>
    </header>
  );
}
